import React, { useEffect, useState } from 'react';
import { useReportService } from '../../services/ReportService';
import useLoadTracker from '@dr-pam/common-components/Hooks/useLoadTracker';
import { Button, Stack, MultiSelect, Text, Divider, RadioGroup, Radio, Group } from '@mantine/core';
import { useForm, schemaResolver } from '@mantine/form';
import { DateInput } from '@mantine/dates';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';
import type { ProductListFragment } from '../../graphql/graphql';
import { useProductService } from '../../services/ProductService';
import z from 'zod';

const validationSchema = z.object({
	productIds: z.array(z.string()).min(1, 'At least one product must be selected'),
	validity: z.enum(['all', 'expired', 'active']),
	validFromStart: z.null().or(z.coerce.date()),
	validFromEnd: z.null().or(z.coerce.date()),
	validToStart: z.null().or(z.coerce.date()),
	validToEnd: z.null().or(z.coerce.date()),
});

type SubscriptionsReportForm = {
	productIds: string[];
	validity: 'all' | 'expired' | 'active';
	validFromStart: Date | null;
	validFromEnd: Date | null;
	validToStart: Date | null;
	validToEnd: Date | null;
};

export type SubscriptionsReportProps = {
	className?: string;
	onReportGenerated: (report: Blob, filenamePrefix: string) => void;
};

export default function SubscriptionsReport(props: SubscriptionsReportProps) {
	const { className, onReportGenerated } = props;

	const { isLoading, addLoader, removeLoader } = useLoadTracker();
	const [products, setProducts] = useState<ProductListFragment[]>([]);

	const reportService = useReportService();
	const productService = useProductService();

	useEffect(() => {
		const loader = addLoader();
		const request = productService.getAll();
		request.response
			.then((products) => {
				setProducts(products);
			})
			.catch((err) => {
				NotificationUtils.showError(err as Error, 'Failed to load products');
			})
			.finally(() => {
				removeLoader(loader);
			});
	}, [addLoader, removeLoader, productService]);

	const form = useForm<SubscriptionsReportForm>({
		initialValues: {
			productIds: [],
			validity: 'all',
			validFromStart: null,
			validFromEnd: null,
			validToStart: null,
			validToEnd: null,
		},
		validate: schemaResolver(validationSchema),
	});

	const handleSubmit = async (values: SubscriptionsReportForm) => {
		const loader = addLoader();
		try {
			const report = await reportService.getSubscriptionsReport({
				productIds: values.productIds,
				expiredOnly: values.validity === 'expired',
				activeOnly: values.validity === 'active',
				validFromStart: values.validFromStart ?? undefined,
				validFromEnd: values.validFromEnd ?? undefined,
				validToStart: values.validToStart ?? undefined,
				validToEnd: values.validToEnd ?? undefined,
			});
			onReportGenerated(report, 'subscriptions_report');
		} catch (err) {
			NotificationUtils.showError(err as Error, 'Failed to generate report');
		} finally {
			removeLoader(loader);
		}
	};

	const productData = products.map((p) => ({
		value: p.id,
		label: p.shortName,
	}));

	return (
		<div className={`ExportOptions ${className ?? ''}`}>
			<form onSubmit={form.onSubmit(handleSubmit)}>
				<Stack maw={640}>
					<Text size="sm">
						This report will generate a CSV file containing all users and their subscriptions for the
						selected products and filters.
					</Text>
					<MultiSelect
						{...form.getInputProps('productIds')}
						data={productData}
						label="Product(s)"
						description="Select one or more products"
						placeholder={isLoading ? 'Loading...' : ''}
						disabled={isLoading}
						withAsterisk
					/>
					<RadioGroup
						{...form.getInputProps('validity')}
						label="Subscription Validity"
						description="Select the validity of the subscriptions to include in the report"
						withAsterisk
					>
						<Group mt="sm">
							<Radio value="all" label="All subscriptions" />
							<Radio value="expired" label="Only expired subscriptions" />
							<Radio value="active" label="Only active subscriptions" />
						</Group>
					</RadioGroup>
					<Divider />
					<Text size="sm">
						You may optionally specify a date range to filter the subscriptions by their validity period.
					</Text>
					<DateInput
						{...form.getInputProps('validFromStart')}
						label="Valid From Start"
						description="Lower date bound for when the subscription commenced"
						valueFormat="DD-MM-YYYY"
						popoverProps={{ withinPortal: true }}
						disabled={isLoading}
						clearable
					/>
					<DateInput
						{...form.getInputProps('validFromEnd')}
						label="Valid From End"
						description="Upper date bound for when the subscription commenced"
						valueFormat="DD-MM-YYYY"
						popoverProps={{ withinPortal: true }}
						disabled={isLoading}
						clearable
					/>
					<DateInput
						{...form.getInputProps('validToStart')}
						label="Valid To Start"
						description="Lower date bound for when the subscription expired"
						valueFormat="DD-MM-YYYY"
						popoverProps={{ withinPortal: true }}
						disabled={isLoading}
						clearable
					/>
					<DateInput
						{...form.getInputProps('validToEnd')}
						label="Valid To End"
						description="Upper date bound for when the subscription expired"
						valueFormat="DD-MM-YYYY"
						popoverProps={{ withinPortal: true }}
						disabled={isLoading}
						clearable
					/>
					<Divider />
					<Button type="submit" className="flex ac jc gxs" loading={isLoading}>
						<span>Generate Report (CSV)</span>
					</Button>
				</Stack>
			</form>
		</div>
	);
}
